import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeftToLine, faArrowRightToLine, faChevronLeft, faLock,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { Nav, NavItem, UncontrolledTooltip } from 'reactstrap';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { URLS } from 'services/urls';
import RenderIf from 'components/RenderIf/RenderIf';
import { IOptionAnyValue } from 'bricks/types';
import { GlobalKeys, useLocalStorage } from 'helpers/localStorage';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import { ISignUpManager } from 'views/public/SignUp/useSignUpManager';
import BackOfEnvelopeKeyValues from 'components/BackOfEnvelopeKeyValues/BackOfEnvelopeKeyValues';
import useAuthContext from 'contexts/AuthContext';
import { useGetBackOfEnvelope } from 'views/DealDashboard/BOE/hooks/useGetBOE';
import { PRO_FORMA_TABLE_NAV, PRO_FORMA_TABS } from '../../constants';
import { NSButton, NSCard } from '../../../../bricks';
import EditProFormaModal from '../ProForma/EditProFormaModal/EditProFormaModal';
import ProFormaSideNavFooter from './ProFormaSideNavFooter/ProFormaSideNavFooter';
import './ProFormaSideNav.scss';
import { useProFormaSocketContext } from '../ProForma/socketContext/ProFormaSocketProvider';

export interface IProFormaSideNavProps {
    activeNav: string;
    navigateToSection: (sideNavName: string, tabName?: string) => void;
    hasWriteAccessToProForma: boolean;
    setSelectedPeriod: (period: IOptionAnyValue) => void;
    selectedPeriod: IOptionAnyValue;
    signUpMode?: boolean;
    signUpManager?: ISignUpManager;
    navigateToProFormaQuickStart?: () => void;
    currentTab?: string;
    setCurrentTab?: (tabName: string) => void;
    setDisplayManualSignUp?: (display: boolean) => void;
}

const ProFormaSideNav = ({
    activeNav,
    navigateToSection,
    setSelectedPeriod,
    selectedPeriod,
    hasWriteAccessToProForma,
    signUpMode = false,
    signUpManager,
    navigateToProFormaQuickStart,
    setCurrentTab,
    currentTab,
    setDisplayManualSignUp,
}: IProFormaSideNavProps) => {
    const { proForma, uses, sources } = useProFormaSocketContext();
    const { selectedOrganizationId } = useAuthContext();

    const navigate = useNavigate();
    const { dealId } = useParams<{ dealId: string }>();
    const [proFormaSideNavCollapsed, setProFormaSideNavCollapsed] = useLocalStorage<boolean>(GlobalKeys.ProFormaSideNavCollapsed, false);
    const [showEditProFormaModal, setShowEditProFormaModal] = useState(false);
    const toggleEditProFormaModal = () => setShowEditProFormaModal(!showEditProFormaModal);
    const [showBackOfEnvelopeView, setShowBackOfEnvelopeView] = useState(false);

    const [isCollapsed, setIsCollapsed] = useState(proFormaSideNavCollapsed);
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [isManuallyCollapsed, setIsManuallyCollapsed] = useState(proFormaSideNavCollapsed);

    const { data: boeScenario } = useGetBackOfEnvelope({
        orgId: selectedOrganizationId!,
        scenarioId: Number(proForma?.boeScenarioId || 0),
        enabled: !!selectedOrganizationId && !!proForma?.boeScenarioId,
    });

    useEffect(() => {
        setIsCollapsed(proFormaSideNavCollapsed);
    }, [proFormaSideNavCollapsed]);

    useEffect(() => {
        if (uses?.totals && sources?.totals && isUsesSourcesImbalanced && activeNav === PRO_FORMA_TABLE_NAV.RETURNS_AND_WATERFALL.name) {
            navigateToSection(PRO_FORMA_TABLE_NAV.PRO_FORMA.name, PRO_FORMA_TABS.DEVELOPMENT.name);
        }
    }, [activeNav, navigateToSection, uses, sources]);

    const isUsesSourcesImbalanced
        = Number(uses?.totals?.amount).toFixed(2) !== Number(sources?.totals?.amount).toFixed(2);

    const isNavItemDisabled = (navName: string) => navName === PRO_FORMA_TABLE_NAV.RETURNS_AND_WATERFALL.name && isUsesSourcesImbalanced;

    const isNavItemLocked = (navName: string) => {
        if (signUpMode) {
            return [
                PRO_FORMA_TABLE_NAV.RETURNS_AND_WATERFALL.name,
                PRO_FORMA_TABLE_NAV.SENSITIVITY.name,
                PRO_FORMA_TABLE_NAV.EXECUTIVE_SUMMARY.name,
            ].includes(navName);
        }
        return false;
    };

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 1200px)');
        const handleResize = () => {
            const smallScreen = mediaQuery.matches;
            setIsSmallScreen(smallScreen);
            setIsCollapsed(smallScreen || isManuallyCollapsed);
        };

        handleResize();
        mediaQuery.addEventListener('change', handleResize);
        return () => mediaQuery.removeEventListener('change', handleResize);
    }, [isManuallyCollapsed]);

    const toggleSideNav = () => {
        if (!isSmallScreen) {
            const newCollapsedState = !isCollapsed;
            setIsManuallyCollapsed(newCollapsedState);
            setIsCollapsed(newCollapsedState);
            setProFormaSideNavCollapsed(newCollapsedState);
        }
    };

    const navItemClass = (tag: string) => {
        const isDisabled = isNavItemDisabled(tag);
        const isLocked = isNavItemLocked(tag);
        if (isLocked || isDisabled) {
            return 'ProFormaSideNav__item--disabled d-flex align-items-center px-4 py-2 justify-content-between';
        }

        return classNames('d-flex align-items-center px-4 py-2 cursor--pointer', {
            'bg-primary-lighten text-primary font-weight-bold': tag === activeNav,
            'text-muted': tag !== activeNav,
        });
    };

    const navigateToProFormaList = () => {
        const path = generatePath(URLS.PRO_FORMA.HOME, { dealId });
        navigate(path);
    };

    const handleExitOrBack = () => {
        if (showBackOfEnvelopeView) {
            setShowBackOfEnvelopeView(false);
        } else {
            navigateToProFormaList();
        }
    };

    const handleViewOriginalScenario = () => {
        setShowBackOfEnvelopeView(true);
    };

    const handleNavItemClick = (navName: string) => {
        if (isNavItemDisabled(navName)) {
            return;
        }
        navigateToSection(navName);
    };

    const hiddenWhenCollapsedClass = classNames({
        'ProFormaSideNav--hidden': isCollapsed,
    });

    useEffect(() => {
        if (isCollapsed) {
            setShowBackOfEnvelopeView(false);
        }
    }, [isCollapsed]);

    return (
        <div className={classNames('ProFormaSideNav-wrapper', {
            'ProFormaSideNav-wrapper--collapsed': isCollapsed,
            'ProFormaSideNav-wrapper--new-user': signUpMode,
        })}
        >
            <NSCard className="NSCard--level-2 ProFormaSideNav">
                <div className={`px-3 pt-3 ${hiddenWhenCollapsedClass}`}>
                    {
                        signUpMode ? (
                            <NSButtonBase
                                callback={navigateToProFormaQuickStart}
                                color="primary"
                                className="font-13 px-2 mb-2"
                                size="sm"
                                outline
                                allowReadOnlyAccess
                            >
                                <FontAwesomeIcon icon={faChevronLeft} className="mr-1" size="xs" />
                                Back
                            </NSButtonBase>
                        ) : (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <NSButton
                                        callback={handleExitOrBack}
                                        color="primary"
                                        className="font-13 px-2"
                                        size="sm"
                                        outline
                                        allowReadOnlyAccess
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} className="mr-1" size="xs" />
                                        {showBackOfEnvelopeView ? 'Back' : 'Exit'}
                                    </NSButton>
                                    <RenderIf isTrue={!showBackOfEnvelopeView && proForma?.boeScenarioId}>
                                        <NSButton
                                            callback={handleViewOriginalScenario}
                                            className="NSButton--secondary-lighten px-2"
                                            size="sm"
                                            outline={false}
                                            allowReadOnlyAccess
                                        >
                                            View original scenario
                                        </NSButton>
                                    </RenderIf>
                                </div>
                                <RenderIf isTrue={showBackOfEnvelopeView && boeScenario}>
                                    <h4 className="text-dark mt-3 mb-0 font-weight-bold">{boeScenario?.name}</h4>
                                </RenderIf>
                                <RenderIf isTrue={!showBackOfEnvelopeView}>
                                    <h4 className="text-dark mt-3 mb-0 font-weight-bold">{proForma?.name}</h4>
                                    <h6 className="mt-1 font-16 text-wrap">{proForma?.description}</h6>
                                    <RenderIf isTrue={hasWriteAccessToProForma}>
                                        <span
                                            role="presentation"
                                            onClick={toggleEditProFormaModal}
                                            className="mt-1 mb-0 font-13 text-primary cursor--pointer"
                                        >
                                            Edit name/description
                                        </span>
                                    </RenderIf>
                                </RenderIf>
                            </>
                        )
                    }

                </div>
                <hr className={`mb-0 ${hiddenWhenCollapsedClass}`} />

                <RenderIf isTrue={showBackOfEnvelopeView && proForma?.boeScenarioId}>
                    <div className="p-3 overflow-auto">
                        <BackOfEnvelopeKeyValues
                            boeScenario={boeScenario}
                            className="BackOfEnvelopeKeyValues__sidenav-height"
                        />
                    </div>
                </RenderIf>

                <RenderIf isTrue={!showBackOfEnvelopeView}>
                    <Nav tabs className="ProFormaSideNav__nav flex-column w-100 border-0">
                        {Object.entries(PRO_FORMA_TABLE_NAV).map(([key, nav]) => (
                            <React.Fragment key={key}>
                                <NavItem
                                    className={navItemClass(nav.name)}
                                    onClick={() => handleNavItemClick(nav.name)}
                                    data-toggle="tab"
                                    id={`nav-item-${key}`}
                                >
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={nav.icon} className="py-1" />
                                        <div className={`pl-1 ${hiddenWhenCollapsedClass}`} aria-hidden="true">
                                            <span>{nav.name}</span>
                                        </div>
                                    </div>
                                    {
                                        isNavItemLocked(nav.name) && (
                                            <FontAwesomeIcon icon={faLock} className="py-1" />
                                        )
                                    }
                                </NavItem>
                                {isNavItemDisabled(nav.name) && !signUpMode && (
                                    <UncontrolledTooltip target={`nav-item-${key}`}>
                                        Returns & Waterfall Analysis will not be available until total uses equal total sources.
                                    </UncontrolledTooltip>
                                )}
                            </React.Fragment>
                        ))}
                    </Nav>
                </RenderIf>
                <ProFormaSideNavFooter
                    setSelectedPeriod={setSelectedPeriod}
                    selectedPeriod={selectedPeriod}
                    isCollapsed={isCollapsed}
                    signUpMode={signUpMode}
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                    signUpManager={signUpManager}
                    setDisplayManualSignUp={setDisplayManualSignUp}
                />

                <RenderIf isTrue={!isSmallScreen && !signUpMode}>
                    <FontAwesomeIcon
                        icon={isCollapsed ? faArrowRightToLine : faArrowLeftToLine}
                        size="sm"
                        className="ProFormaSideNav__toggle-button text-dark p-2 cursor--pointer rounded"
                        onClick={toggleSideNav}
                    />
                </RenderIf>
            </NSCard>
            <EditProFormaModal showEditProFormaModal={showEditProFormaModal} toggleEditProFormaModal={toggleEditProFormaModal} />
        </div>
    );
};

export default ProFormaSideNav;
